import { ReactNode } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Trophy } from "lucide-react";
import type { LunimarRealm } from "@/components/lunimar/lunimarRealms";

interface Props {
  realm?: LunimarRealm;
  title?: string;
  /** Eyebrow label shown above the title — e.g. "Mission Debrief". */
  eyebrow?: string;
  /** Outcome label like "Capital Strategist". */
  outcome?: string;
  children: ReactNode;
}

/**
 * End-of-module debrief container. Wrap ReadinessMeter, ModuleRecapCard,
 * SimWalletImpact, ParentInsightSummary, etc. inside this.
 */
const MissionResultPanel = ({
  realm,
  title = "Mission complete",
  eyebrow = "Mission Debrief",
  outcome,
  children,
}: Props) => {
  const reduce = useReducedMotion();
  const accent = realm?.primaryAccent ?? "210 90% 64%";
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: reduce ? 0 : 0.5 }}
      className="relative overflow-hidden rounded-3xl border p-5 backdrop-blur-md sm:p-7"
      style={{
        borderColor: `hsl(${accent} / 0.35)`,
        background: `linear-gradient(160deg, hsl(${accent} / 0.10), hsl(228 45% 10% / 0.7))`,
        boxShadow: `0 40px 80px -40px hsl(${accent} / 0.5)`,
      }}
    >
      <div
        aria-hidden
        className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full opacity-25 blur-3xl"
        style={{ background: `hsl(${accent})` }}
      />
      <div className="relative">
        <div
          className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest"
          style={{ color: `hsl(${accent})` }}
        >
          <Trophy className="h-3 w-3" />
          {eyebrow}
        </div>
        <div className="mt-1 flex flex-wrap items-baseline justify-between gap-3">
          <h3 className="font-heading text-xl font-bold text-white sm:text-2xl">{title}</h3>
          {outcome && (
            <span
              className="rounded-md border px-2 py-0.5 font-mono text-[11px] uppercase tracking-wider"
              style={{
                borderColor: `hsl(${accent} / 0.5)`,
                background: `hsl(${accent} / 0.14)`,
                color: `hsl(${accent})`,
              }}
            >
              {outcome}
            </span>
          )}
        </div>
        <div className="mt-5 space-y-4">{children}</div>
      </div>
    </motion.div>
  );
};

export default MissionResultPanel;